import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Alert,
    LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SelectChangeEvent } from '@mui/material/Select';
import { useMutation, gql } from '@apollo/client';
import CheckCircle from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';

// Indian PIN codes are six digits and never start with 0. The invoice's GST
// place of supply is derived from this, so a malformed value silently bills the
// customer as if they were in our own state. The backend rejects it too.
const isValidPincode = (value: string): boolean => /^[1-9][0-9]{5}$/.test(value.trim());

interface ManualKycDialogProps {
    open: boolean;
    onClose: () => void;
    vehicleId: string;
    data: {
        vin: string;
        imei: string;
        engine_no: string;
        iccid: string;
        device_model: string;
        vehicle_id: string;
        dealer_id: string;
        dealer_name?: string;
        dealer_contact_no?: string;
    };
}

const proofOfIdentityTypes = ['Aadhar Card', 'Passport', 'PAN Card', 'Driving License', 'Voter ID'];
const proofOfAddressTypes = ['Aadhar Card', 'Passport', 'Driving License', 'Utility Bill', 'Bank Statement', 'Rent Agreement'];
const DEFAULT_KYC_ACTION = 'MKYC_UPDATE';

const RAISE_KYC_REQ = gql`
mutation RaiseKycReq($input: KYCRequestInput!, $vehicleId: String!, $action: KYCAction!) {
  raiseKycReq(input: $input, vehicleId: $vehicleId, action: $action) {
    success
    kyc_id
    message
    error
  }
}
`;

const UPLOAD_IMAGE = gql`
  mutation UploadImage($image: Upload!) {
    uploadImage(image: $image) {
      success
      url
      error
    }
  }
`;

interface KycFileUploadProps {
    label: string;
    field: 'poa_front' | 'poa_back' | 'poi_front' | 'poi_back';
    url: string | null;
    onUploadSuccess: (field: 'poa_front' | 'poa_back' | 'poi_front' | 'poi_back', url: string) => void;
    onRemove: (field: 'poa_front' | 'poa_back' | 'poi_front' | 'poi_back') => void;
}

const KycFileUpload: React.FC<KycFileUploadProps> = ({
    label,
    field,
    url,
    onUploadSuccess,
    onRemove
}) => {
    const [fileName, setFileName] = useState<string>('');
    const [uploading, setUploading] = useState(false);

    const [uploadImage] = useMutation(UPLOAD_IMAGE);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
        const fileExtension = selectedFile.name.toLowerCase().split('.').pop();

        if (!allowedTypes.includes(selectedFile.type) ||
            !['png', 'jpg', 'jpeg'].includes(fileExtension || '')) {
            alert('Only image files are allowed (PNG, JPG, JPEG).');
            return;
        }


        if (selectedFile.size > 50 * 1024 * 1024) {
            alert('File size should not exceed 50MB.');
            return;
        }

        setFileName(selectedFile.name);
        setUploading(true);

        try {
            const { data } = await uploadImage({ variables: { image: selectedFile } });

            if (data?.uploadImage?.success && data.uploadImage.url) {
                onUploadSuccess(field, data.uploadImage.url);
            } else {
                alert(data?.uploadImage?.error || 'Upload failed');
                setFileName('');
            }
        } catch (err: any) {
            alert('File upload failed: ' + (err.message || 'Unknown error'));
            setFileName('');
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = () => {
        setFileName('');
        onRemove(field);
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {label} <span style={{ color: 'red' }}>*</span>
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    p: 1.5,
                    background: '#fff',
                    minHeight: 56
                }}
            >
                <input
                    id={`upload-${field}`}
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
                <label htmlFor={`upload-${field}`}>
                    <Button
                        variant="contained"
                        component="span"
                        sx={{ backgroundColor: '#000', '&:hover': { backgroundColor: '#333' } }}
                        disabled={uploading}
                    >
                        Choose Image
                    </Button>
                </label>

                {(fileName || url) && (
                    <Box sx={{ ml: 2, flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                            sx={{
                                fontSize: 14,
                                flex: 1,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word'
                            }}
                        >
                            {fileName || 'File uploaded successfully'}
                        </Typography>
                        {url && <CheckCircle sx={{ color: 'green', fontSize: 20 }} />}
                    </Box>
                )}

                {(fileName || url) && (
                    <IconButton
                        onClick={handleRemove}
                        color="error"
                        size="small"
                        title="Remove file"
                    >
                        <DeleteIcon />
                    </IconButton>
                )}
            </Box>

            {uploading && <LinearProgress sx={{ mt: 1 }} />}
        </Box>
    );
};

const ManualKycDialog: React.FC<ManualKycDialogProps> = ({ open, onClose, data }) => {
    const [raiseKycReq, { loading: submitLoading }] = useMutation(RAISE_KYC_REQ);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    const [form, setForm] = useState({
        engine_no: data.engine_no || 'N/A',
        imei: data.imei || 'N/A',
        chassis_no: data.vin || 'N/A',
        iccid: data.iccid || 'N/A',
        device_model: data.device_model || 'N/A',
        device_serial_no: data.imei || 'N/A',
        vehicle_id: data.vehicle_id || '',
        dealer_id: data.dealer_id || '',
        dealer_name: data.dealer_name || 'N/A',
        dealer_contact_no: data.dealer_contact_no || 'N/A',

        proofOfIdentity: '',
        proofOfAddress: '',
        kyc_mode: 'MANUAL',
        kyc_source: 'OEM_TOOL',

        cus_name: '',
        cus_cont_no: '',
        cus_pincode: '',
        cus_adrs: '',

        poa_id_number: '',
        poa_front: null as string | null,
        poa_back: null as string | null,

        poi_id_number: '',
        poi_front: null as string | null,
        poi_back: null as string | null,
    });

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name as string]: value }));
    };

    const handleSelectChange = (e: SelectChangeEvent) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name as string]: value }));
    };

    const updateFile = (field: 'poa_front' | 'poa_back' | 'poi_front' | 'poi_back', url: string) => {
        setForm(prev => ({ ...prev, [field]: url }));
    };

    const removeFile = (field: 'poa_front' | 'poa_back' | 'poi_front' | 'poi_back') => {
        setForm(prev => ({ ...prev, [field]: null }));
    };

    const handleSubmit = async () => {
        setSuccessMsg('');
        setErrorMsg('');

        // The device block is deliberately NOT sent.
        //
        // KYCRequestInput declares no engine_no / imei / chassis_no / iccid /
        // device_model / device_serial_no. GraphQL rejects an input object
        // carrying undeclared fields during variable coercion -- before the
        // resolver runs -- so every submission from this dialog died with
        // 'Field "engine_no" is not defined by type "KYCRequestInput"'. That is
        // why kyc_details holds zero Mkyc-Updates: nothing ever reached the
        // server, let alone Sensorise. Verified against production 2026-09-09.
        //
        // The fields are still shown above, read-only, because the operator
        // needs to see which device is being corrected. The server reads them
        // itself from vehicle_details / tcu_inventory / sim_inventory by
        // vehicleId and its own values override the caller's regardless.
        const input = {
            proofOfIdentity: form.proofOfIdentity,
            proofOfAddress: form.proofOfAddress,
            kyc_mode: form.kyc_mode,
            kyc_source: form.kyc_source,

            cus_name: form.cus_name,
            cus_cont_no: form.cus_cont_no,
            cus_pincode: form.cus_pincode,
            cus_adrs: form.cus_adrs,

            poa_id_number: form.poa_id_number,
            poa_front: form.poa_front,
            poa_back: form.poa_back,

            poi_id_number: form.poi_id_number,
            poi_front: form.poi_front,
            poi_back: form.poi_back,
            dealer_id: form.dealer_id,

        };

        try {
            const response = await raiseKycReq({
                variables: { input, vehicleId: form.vehicle_id, action: DEFAULT_KYC_ACTION }
            });

            if (response.data?.raiseKycReq?.success) {
                setSuccessMsg(response.data.raiseKycReq.message || 'KYC request raised successfully!');
                setTimeout(onClose, 1600);
            } else {
                setErrorMsg(response.data?.raiseKycReq?.error || response.data?.raiseKycReq?.message || 'Failed to submit');
            }
        } catch (err: any) {
            setErrorMsg(err.message || 'Something went wrong');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h5" fontWeight="bold">Create KYC Details</Typography>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent>
                {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
                {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2.5, pt: 2 }}>

                    {/* Disabled Fields */}
                    <TextField label="VIN" value={form.chassis_no} disabled fullWidth />
                    <TextField label="IMEI" value={form.imei} disabled fullWidth />
                    <TextField label="Engine No" value={form.engine_no} disabled fullWidth />
                    <TextField label="ICCID" value={form.iccid} disabled fullWidth />
                    <TextField label="Device Model" value={form.device_model} disabled fullWidth />
                    <TextField label="Device Serial No" value={form.device_serial_no} disabled fullWidth />
                    <TextField label="KYC Mode" value={form.kyc_mode} disabled fullWidth />
                    <TextField label="KYC Source" value={form.kyc_source} disabled fullWidth />
                    <TextField label="Dealer Name" value={form.dealer_name} disabled fullWidth />
                    <TextField label="Dealer Contact No" value={form.dealer_contact_no} disabled fullWidth />

                    <TextField label="Customer Name *" name="cus_name" value={form.cus_name} onChange={handleTextChange} fullWidth />
                    <TextField label="Customer Contact No *" name="cus_cont_no" value={form.cus_cont_no} onChange={handleTextChange} fullWidth />
                    <TextField
                        label="Customer Pincode"
                        name="cus_pincode"
                        value={form.cus_pincode}
                        onChange={handleTextChange}
                        error={form.cus_pincode.trim().length > 0 && !isValidPincode(form.cus_pincode)}
                        helperText={
                            form.cus_pincode.trim().length > 0 && !isValidPincode(form.cus_pincode)
                                ? 'Enter a valid 6-digit PIN code'
                                : ' '
                        }
                        inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                        fullWidth
                    />
                    <TextField label="Customer Address *" name="cus_adrs" value={form.cus_adrs} onChange={handleTextChange} fullWidth />

                    <FormControl fullWidth>
                        <InputLabel>Proof of Address *</InputLabel>
                        <Select name="proofOfAddress" value={form.proofOfAddress} onChange={handleSelectChange} label="Proof of Address *">
                            {proofOfAddressTypes.map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel>Proof of Identity *</InputLabel>
                        <Select name="proofOfIdentity" value={form.proofOfIdentity} onChange={handleSelectChange} label="Proof of Identity *">
                            {proofOfIdentityTypes.map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField label="POA ID Number *" name="poa_id_number" value={form.poa_id_number} onChange={handleTextChange} fullWidth inputProps={{ maxLength: 32 }}/>
                    <TextField label="POI ID Number *" name="poi_id_number" value={form.poi_id_number} onChange={handleTextChange} fullWidth  inputProps={{ maxLength: 32 }}/>

                    {/* KYC File Uploads - Now using updated component */}
                    <KycFileUpload label="POA Front" field="poa_front" url={form.poa_front} onUploadSuccess={updateFile} onRemove={removeFile} />
                    <KycFileUpload label="POA Back" field="poa_back" url={form.poa_back} onUploadSuccess={updateFile} onRemove={removeFile} />
                    <KycFileUpload label="POI Front" field="poi_front" url={form.poi_front} onUploadSuccess={updateFile} onRemove={removeFile} />
                    <KycFileUpload label="POI Back" field="poi_back" url={form.poi_back} onUploadSuccess={updateFile} onRemove={removeFile} />
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button onClick={onClose} variant="outlined" disabled={submitLoading}>CANCEL</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="error"
                    disabled={submitLoading || !form.cus_name.trim() || !form.cus_cont_no.trim() || (form.cus_pincode.trim().length > 0 && !isValidPincode(form.cus_pincode)) || !form.cus_adrs.trim() || !form.proofOfAddress || !form.proofOfIdentity || !form.poa_id_number.trim() || !form.poi_id_number.trim() || !form.poa_front || !form.poi_front}
                >
                    {submitLoading ? 'Submitting...' : 'Submit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ManualKycDialog;